import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { FilterService } from '@services/filter.service';
import { DirectivesModule } from 'src/app/directive/auto-complete-off/auto-complete-off.module';

/** マクニカ管理者向けテーブルカラム */
export const DEVICE_TYPE_TABLE_COLUMN_LIST: string[] = [
  'no',
  'deviceTypeId',
  'deviceTypeCount',
  'description',
  'operation',
] as const;

/** デバイス種類のViewModel */
export type DeviceTypeViewModel = {
  /** システム内部のデバイス種類キー */
  deviceTypeKey: string;
  /** デバイス種類ID */
  deviceTypeId: string;
  /** 表示名 */
  displayName: string;
  /** デバイス数 */
  deviceTypeCount?: number;
  /** 説明 */
  description?: string;
  // センサーデータ/クラウドデータのデータ受信はnumber[]で受け取るがデバイス新規登録or編集ダイアログではstring型としてデータを受け取る、
  /** センサーデータ収集間隔選択肢(CSV形式)*/
  samplingFrequency?: number[];
  /** クラウドへのデータ送信間隔選択肢(CSV形式) */
  reportingFrequency?: number[];
  /** 温度センサー */
  temperatureSensor?: boolean;
  /** 気圧センサー */
  pressureSensor?: boolean;
  /** 湿度センサー */
  humiditySensor?: boolean;
  /** 傾斜センサー */
  tiltSensor?: boolean;
  /** 照度センサー */
  lightSensor?: boolean;
  /** 位置即位 */
  locationSensor?: boolean;
  /** 位置即位技術(GNSS) */
  locationTechGnss?: boolean;
  /** 位置即位技術(Wi-Fi) */
  locationTechWifi?: boolean;
  /** 位置即位技術(Cellular) */
  locationTechCellular?: boolean;
  /** 落下検知 */
  fallDetected?: boolean;
  /** 動作検知 */
  mcdDetected?: boolean;
  /** 明暗検知 */
  lightExposureDetected?: boolean;
  /** 衝撃検知 */
  shockDetected?: boolean;
  /** フライトモード */
  fmd?: boolean;
};

interface DeviceTypeListFilter {
  displayName?: string;
  description?: string;
  sortState?: Sort;
  pageIndex?: number;
}

const pageId = 'DeviceTypeList';

@Component({
  selector: 'app-device-type-list-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatSortModule,
    MatTableModule,
    MatButtonModule,
    MatSortModule,
    MatPaginatorModule,
    DirectivesModule,
  ],
  templateUrl: './device-type-list-table.component.html',
  styleUrl: './device-type-list-table.component.scss',
})
export class DeviceTypeListTableComponent
  implements OnInit, AfterViewInit, OnChanges
{
  /** 新規登録を通知 */
  @Output() created = new EventEmitter<void>();

  /** 更新を通知 */
  @Output() updated = new EventEmitter<DeviceTypeViewModel>();

  /** 削除を通知 */
  @Output() deleted = new EventEmitter<string>();

  /** ページネーション要素 */
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  /** ソート要素 */
  @ViewChild(MatSort) sort!: MatSort;

  /** デバイス種類検索キーワード */
  public displayNameKeyword = '';

  /** 説明検索キーワード */
  public descriptionKeyword = '';

  /** デバイス種類一覧 */
  @Input({ required: true }) public deviceTypeList: DeviceTypeViewModel[] = [];

  /** カラム */
  public displayedColumns: string[] = DEVICE_TYPE_TABLE_COLUMN_LIST;

  /** 一覧情報 */
  public dataSource = new MatTableDataSource<DeviceTypeViewModel>([]);

  /** 初期ソート状態 */
  public initSortState: Sort = {
    active: 'displayName',
    direction: 'asc',
  };

  /** ソート状態 */
  private sortState: Sort | undefined;

  /** ページ番号 */
  private pageIndex: number | undefined;

  constructor(
    private filterService: FilterService<
      DeviceTypeListFilter,
      DeviceTypeViewModel
    >,
    private cdr: ChangeDetectorRef,
  ) {
    this.filterService.setSessionStorageKey(pageId);
  }

  public ngOnInit(): void {
    this.dataSource.data = this.deviceTypeList;
    this.dataSource.sort = this.sort;

    // フィルター条件の取得と適用
    this.loadFilters();
  }

  public ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    // ソートとページングの変更を検知する
    this.filterService.monitorSortAndPagination(this.sort, this.paginator);

    // ソートとページングの設定
    if (this.sort && this.sortState) {
      const sortState: Sort = {
        active: this.sortState.active,
        direction: this.sortState.direction,
      };

      this.filterService.setSort(this.dataSource, this.sort, sortState);
    }

    // ページングを設定
    if (this.paginator && this.pageIndex) {
      this.setPaginationIndex(this.pageIndex);
    }

    // フィルター設定
    this.dataSource.filterPredicate = (element: DeviceTypeViewModel) => {
      let description = true;
      if (this.descriptionKeyword) {
        // descriptionKeywordが空でない場合のみフィルタリングを行う
        description =
          (element.description ?? '')
            .trim()
            .toLowerCase()
            .indexOf(this.descriptionKeyword.toLowerCase()) > -1;
      }
      let displayName = true;
      if (this.displayNameKeyword) {
        // displayNameKeywordが空でない場合のみフィルタリングを行う
        displayName =
          element.displayName
            .trim()
            .toLowerCase()
            .indexOf(this.displayNameKeyword.toLowerCase()) > -1;
      }
      // フィルター条件を保持
      this.saveFiltersToStorage();

      return displayName && description;
    };
    this.applyFilter();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['deviceTypeList'] !== undefined) {
      const deviceTypeList: DeviceTypeViewModel[] =
        changes['deviceTypeList'].currentValue;
      this.dataSource.data = deviceTypeList;
    }
  }

  /**
   * 検索条件（FilterPredicate）に基づいて、フィルタリング処理を適用する。
   */
  public applyFilter(): void {
    // フィルタ処理を発火
    // ※空文字指定だとフィルタリングがされないためdummy文字列を指定している
    // （Angular materialのtable-data-source.tsの_filterDataメソッド参照）
    this.dataSource.filter = 'dummy';
  }

  /**
   * 表示名でのフィルタリングをクリアする。
   */
  public clearDisplayNameFilter(): void {
    this.displayNameKeyword = '';

    this.applyFilter();
  }

  /**
   * 説明でのフィルタリングをクリアする。
   */
  public clearDescriptionNameFilter(): void {
    // フィルタ条件をクリア
    this.descriptionKeyword = '';

    this.applyFilter();
  }

  /**
   * 各フィルターを初期化する。
   */
  public clearFilters() {
    this.loadFilters(true);

    const sortState: Sort = this.initSortState;

    this.filterService.setSort(this.dataSource, this.sort, sortState);

    // フィルターを適用
    this.dataSource.filter = '';
    this.applyFilter();
  }

  /**
   * ページネーションの番号を設定する
   */
  private setPaginationIndex(pageIndex: number = 0) {
    setTimeout(() => {
      this.paginator.pageIndex = pageIndex!;
      this.cdr.detectChanges();
    }, 0);
  }

  /**
   * フィルターをセッションストレージから読み込む。
   */
  private loadFilters(reset?: boolean): void {
    const filters = this.filterService.getFiltersFromSessionStorage(reset);
    this.displayNameKeyword = filters.displayName || '';
    this.descriptionKeyword = filters.description || '';
    this.sortState = filters.sortState;
    this.pageIndex = filters.pageIndex;
  }

  /**
   * フィルター条件をセッションストレージに保存する
   */
  private saveFiltersToStorage(): void {
    const filterList: DeviceTypeListFilter = {
      displayName: this.displayNameKeyword,
      description: this.descriptionKeyword,
    };
    this.filterService.setFiltersToSessionStorage(filterList);
  }
}
